import React, { useState, useEffect } from 'react';
import { Cookie, Close, Settings } from '@mui/icons-material';

const CookieBanner = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState({
        essential: true,
        analytics: false,
        preferences: false
    });

    // ✅ Initialisation UNE SEULE FOIS (corrige la boucle infinie)
    useEffect(() => {
        const consent = localStorage.getItem('cookieConsent');

        if (!consent) {
            setIsVisible(true);
            return;
        }

        try {
            const savedPrefs = JSON.parse(localStorage.getItem('cookiePreferences'));
            if (savedPrefs) {
                setPreferences(prev => ({
                    ...prev,
                    ...savedPrefs
                }));
            }
        } catch {
            // erreur ignorée volontairement
        }
    }, []);

    const savePreferences = (prefs) => {
        localStorage.setItem('cookieConsent', 'true');
        localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
    };

    const handleAcceptAll = () => {
        const allAccepted = {
            essential: true,
            analytics: true,
            preferences: true
        };
        setPreferences(allAccepted);
        savePreferences(allAccepted);
        setIsVisible(false);
    };

    const handleRejectAll = () => {
        const onlyEssential = {
            essential: true,
            analytics: false,
            preferences: false
        };
        setPreferences(onlyEssential);
        savePreferences(onlyEssential);
        setIsVisible(false);
    };

    const handleSavePreferences = () => {
        savePreferences(preferences);
        setIsVisible(false);
        setShowSettings(false);
    };

    if (!isVisible) return null;

    return (
        <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/50 z-[70]" />

            {/* Cookie Banner */}
            <div className="fixed bottom-0 left-0 right-0 z-[80] p-4 md:p-6">
                <div className="max-w-6xl mx-auto">
                    {!showSettings ? (
                        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 md:p-8 dark:border-slate-600 dark:bg-slate-800">
                            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0 dark:bg-amber-500/20">
                                    <Cookie className="text-amber-600 dark:text-amber-400" />
                                </div>

                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900 mb-2 dark:text-slate-100">
                                        Nous utilisons des cookies
                                    </h3>
                                    <p className="text-gray-700 text-sm mb-4 dark:text-slate-300">
                                        Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic du site
                                        et personnaliser le contenu. Vous pouvez accepter tous les cookies ou personnaliser
                                        vos préférences.
                                    </p>

                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            onClick={handleAcceptAll}
                                            className="px-6 py-2 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all"
                                        >
                                            Accepter tout
                                        </button>

                                        <button
                                            onClick={handleRejectAll}
                                            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                                        >
                                            Refuser tout
                                        </button>

                                        <button
                                            onClick={() => setShowSettings(true)}
                                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all flex items-center space-x-2 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                                        >
                                            <Settings fontSize="small" />
                                            <span>Personnaliser</span>
                                        </button>

                                        <a
                                            href="/cookies"
                                            className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium flex items-center dark:text-slate-400 dark:hover:text-slate-200"
                                        >
                                            En savoir plus
                                        </a>
                                    </div>
                                </div>

                                <button
                                    onClick={handleRejectAll}
                                    className="text-gray-400 hover:text-gray-600 transition-colors dark:text-slate-500 dark:hover:text-slate-300"
                                    aria-label="Fermer"
                                >
                                    <Close />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 md:p-8 max-h-[80vh] overflow-y-auto dark:border-slate-600 dark:bg-slate-800">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                                    <Settings className="text-amber-600" />
                                    <span>Paramètres des cookies</span>
                                </h3>
                                <button
                                    onClick={() => setShowSettings(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors dark:text-slate-500 dark:hover:text-slate-300"
                                    aria-label="Fermer"
                                >
                                    <Close />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Cookies essentiels */}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <h4 className="font-semibold text-gray-900">Cookies essentiels</h4>
                                            <p className="text-sm text-gray-600">Toujours actifs</p>
                                        </div>
                                        <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-semibold">
                                            Obligatoire
                                        </span>
                                    </div>
                                </div>

                                {/* Cookies analytiques */}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <h4 className="font-semibold text-gray-900">Cookies analytiques</h4>
                                            <p className="text-sm text-gray-600">Analyse d’audience</p>
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={preferences.analytics}
                                            onChange={(e) =>
                                                setPreferences(prev => ({
                                                    ...prev,
                                                    analytics: e.target.checked
                                                }))
                                            }
                                        />
                                    </div>
                                </div>

                                {/* Cookies de préférences */}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <h4 className="font-semibold text-gray-900">Cookies de préférences</h4>
                                            <p className="text-sm text-gray-600">Personnalisation</p>
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={preferences.preferences}
                                            onChange={(e) =>
                                                setPreferences(prev => ({
                                                    ...prev,
                                                    preferences: e.target.checked
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-200">
                                <button
                                    onClick={handleSavePreferences}
                                    className="px-6 py-2 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all"
                                >
                                    Enregistrer
                                </button>

                                <button
                                    onClick={handleAcceptAll}
                                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all"
                                >
                                    Accepter tout
                                </button>

                                <button
                                    onClick={handleRejectAll}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all"
                                >
                                    Refuser tout
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CookieBanner;
